import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { listJobs } from '@/api/jobs';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { useAuthStore } from '@/stores/authStore';
import type { JobOut } from '@/types';
import { dueCountdown, formatDateTime } from '@/utils/due';

import styles from './todo.module.css';

interface TodoItem {
  job: JobOut;
  kind: 'submit' | 'view';
}

export function TodoPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isStudent = user?.role === 'student';

  const { data, isLoading } = useQuery({
    queryKey: ['jobs', 'todo'],
    queryFn: () => listJobs({ limit: 200 }),
    enabled: isStudent,
  });

  if (!isStudent) {
    return <div className={styles.empty}>待办中心是学生功能，教师请前往批阅中心。</div>;
  }

  const todos: TodoItem[] = [];
  for (const job of data?.items ?? []) {
    if (job.status !== 'published') continue;
    if (!job.my_submission_status) {
      // 未提交
      todos.push({ job, kind: 'submit' });
    } else if (
      job.my_submission_status === 'reviewed' ||
      job.review_status === 'confirmed' ||
      job.review_status === 'modified'
    ) {
      // 已批阅待查看
      todos.push({ job, kind: 'view' });
    } else if (job.review_status === 'rejected') {
      // 被打回需重新提交
      todos.push({ job, kind: 'submit' });
    }
  }

  const submitTodos = todos.filter((t) => t.kind === 'submit');
  const viewTodos = todos.filter((t) => t.kind === 'view');
  const overdueCount = submitTodos.filter((t) => dueCountdown(t.job.due_date).level === 'overdue').length;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>待办中心</h1>
          <p className={styles.subtitle}>
            待提交 {submitTodos.length} 项 · 待查看批改 {viewTodos.length} 项
            {overdueCount > 0 && <span className={styles.overdueTag}>（{overdueCount} 项已逾期）</span>}
          </p>
        </div>
      </header>

      {isLoading && <div className={styles.empty}>加载中...</div>}

      {!isLoading && todos.length === 0 && (
        <div className={styles.empty}>太棒了，当前没有待处理的作业！🎉</div>
      )}

      {submitTodos.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>待提交</h2>
          <div className={styles.list}>
            {submitTodos.map(({ job }) => {
              const cd = dueCountdown(job.due_date);
              return (
                <Card key={`s-${job.id}`} className={styles.todoCard}>
                  <div className={styles.todoInfo}>
                    <span className={styles.todoTitle}>{job.title}</span>
                    <span className={styles.todoMeta}>
                      {job.subject} · 满分 {job.max_score}
                      <br />
                      布置 {formatDateTime(job.created_at)} · 截止{' '}
                      {job.due_date ? formatDateTime(job.due_date) : '未设置'}
                    </span>
                  </div>
                  {job.due_date && (
                    <span
                      className={`${styles.countdown} ${
                        cd.level === 'overdue'
                          ? styles.cdOverdue
                          : cd.level === 'urgent'
                            ? styles.cdUrgent
                            : ''
                      }`}
                    >
                      {cd.text}
                    </span>
                  )}
                  <Button
                    size="small"
                    variant={cd.level === 'overdue' ? 'secondary' : 'primary'}
                    onClick={() => navigate(`/jobs/${job.id}/submit`)}
                  >
                    {job.review_status === 'rejected' ? '重新提交' : '去提交'}
                  </Button>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {viewTodos.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>待查看批改</h2>
          <div className={styles.list}>
            {viewTodos.map(({ job }) => (
              <Card key={`v-${job.id}`} className={styles.todoCard}>
                <div className={styles.todoInfo}>
                  <span className={styles.todoTitle}>{job.title}</span>
                  <span className={styles.todoMeta}>
                    得分 {job.my_score ?? '-'}/{job.max_score}
                  </span>
                </div>
                <Button size="small" onClick={() => navigate(`/jobs/${job.id}`)}>
                  查看 AI 报告
                </Button>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
